import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';

import { Card, CardContent } from '@/components/health-ui';

type ErrorStateTone = 'primary' | 'danger';

interface ErrorStateProps {
  tone: ErrorStateTone;
  icon: LucideIcon;
  code?: string;
  headline: string;
  description: string;
  actions: ReactNode[];
  referenceId?: string;
  compact?: boolean;
}

const toneStyles: Record<ErrorStateTone, string> = {
  primary: 'bg-primary/10 text-primary',
  danger: 'bg-status-unmatched-bg text-status-unmatched',
};

export function ErrorState({
  tone,
  icon: Icon,
  code,
  headline,
  description,
  actions,
  referenceId,
  compact,
}: Readonly<ErrorStateProps>): React.JSX.Element {
  const containerClass = compact
    ? 'p-4 md:p-6 lg:p-8'
    : 'flex min-h-[calc(100svh-4rem)] items-center justify-center p-4 md:p-6 lg:p-8';

  const contentWidth = compact ? 'w-full' : 'w-full max-w-xl';

  return (
    <div className={containerClass}>
      <Card className={`${contentWidth} border-0 bg-transparent shadow-none`}>
        <CardContent className="flex flex-col items-center gap-6 p-4 text-center md:p-6 lg:p-8">
          {/* Icon badge */}
          <div
            className={`flex h-[88px] w-[88px] items-center justify-center rounded-full border-0 ${toneStyles[tone]}`}
            aria-hidden
          >
            <Icon className="h-[38px] w-[38px]" />
          </div>

          {/* Code, headline, description */}
          <div className="space-y-3">
            {code && (
              <p className="font-display text-5xl font-bold leading-tight tracking-tight text-foreground">
                {code}
              </p>
            )}
            <h1 className="font-display text-2xl font-semibold text-foreground">
              {headline}
            </h1>
            <p className="font-sans text-sm leading-6 text-muted-foreground">
              {description}
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {actions.map((action, i) => (
              <div key={i}>{action}</div>
            ))}
          </div>

          {/* Reference bar (only when ID present) */}
          {referenceId && (
            <div className="rounded-lg border border-border bg-muted p-2 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Error reference:</span>
                <span className="font-medium text-foreground">
                  {/* screen-id-allowed(kevin@91.life): the error reference a user reads out to support */}
                  {referenceId}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
